import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Cpu, Database, Zap, Activity, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AGENT_TOOLS, AGENT_ID, AGENT_PROMPT } from '@/config/agent-tools';

export default function AgentStatusDashboard() {
  const [agentStatus, setAgentStatus] = useState({
    isOnline: true,
    lastActivity: new Date(),
    totalInteractions: 0,
    activeThreads: 0,
  });

  const [systemMetrics, setSystemMetrics] = useState({
    avgResponseTime: 0,
    successRate: 0,
    totalToolCalls: 0,
  });

  useEffect(() => {
    loadAgentMetrics();
    const interval = setInterval(loadAgentMetrics, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadAgentMetrics = async () => {
    try {
      // Get recent AI usage logs for metrics - include both voice_agent and agent_control_chat
      const { data: logs } = await supabase
        .from('ai_usage_logs')
        .select('*')
        .in('channel', ['voice_agent', 'agent_control_chat'])
        .order('created_at', { ascending: false })
        .limit(100);

      if (logs) {
        const successful = logs.filter(log => log.status === 'completed').length;
        const successRate = logs.length > 0 ? (successful / logs.length) * 100 : 0;

        setSystemMetrics({
          avgResponseTime: 1200, // Placeholder
          successRate: successRate,
          totalToolCalls: logs.length,
        });

        setAgentStatus({
          isOnline: true,
          lastActivity: logs[0]?.created_at ? new Date(logs[0].created_at) : new Date(),
          totalInteractions: logs.length,
          activeThreads: 0,
        });
      }
    } catch (error) {
      console.error('Failed to load agent metrics:', error);
    }
  };

  const toolCount = Object.keys(AGENT_TOOLS).length;
  const capabilities = [
    'Job Management',
    'Lead Creation',
    'Estimate Generation',
    'Appointment Scheduling',
    'Measurement Retrieval',
    'Daily Briefings',
    'Customer Follow-ups',
    'Xactimate Code Lookup',
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agent Status</CardTitle>
            {agentStatus.isOnline ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agentStatus.isOnline ? 'Online' : 'Offline'}
            </div>
            <p className="text-xs text-muted-foreground">
              Last activity: {agentStatus.lastActivity.toLocaleTimeString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Tools</CardTitle>
            <Zap className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{toolCount}</div>
            <p className="text-xs text-muted-foreground">Active function calls</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <Activity className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{systemMetrics.successRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {systemMetrics.totalToolCalls} total calls
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response</CardTitle>
            <Clock className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{systemMetrics.avgResponseTime}ms</div>
            <p className="text-xs text-muted-foreground">Last 100 interactions</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cpu className="h-5 w-5" />
              Agent Configuration
            </CardTitle>
            <CardDescription>Core system parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Agent ID</span>
                <Badge variant="outline">{AGENT_ID}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Model</span>
                <Badge>ElevenLabs Conversational AI</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Voice</span>
                <Badge variant="secondary">Alloy (OpenAI Compatible)</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Context Window</span>
                <Badge variant="outline">Unlimited Conversation</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Memory Type</span>
                <Badge variant="secondary">PostgreSQL + Agent Memory</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Capabilities
            </CardTitle>
            <CardDescription>Available agent actions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {capabilities.map((cap) => (
                <Badge key={cap} variant="outline">
                  {cap}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active System Prompt</CardTitle>
          <CardDescription>Current instructions governing agent behavior</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-muted rounded-lg p-4 max-h-96 overflow-y-auto">
            <pre className="text-xs whitespace-pre-wrap font-mono">{AGENT_PROMPT}</pre>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Registered Tools</CardTitle>
          <CardDescription>All functions Bridgette can execute</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(AGENT_TOOLS).map(([key, tool]) => (
              <div key={key} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-sm">{tool.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1">{tool.description}</p>
                  </div>
                  <Badge variant="outline" className="ml-2">
                    {Object.keys(tool.parameters || {}).length} params
                  </Badge>
                </div>
                {tool.parameters && Object.keys(tool.parameters).length > 0 && (
                  <div className="mt-2 bg-muted/50 rounded p-2">
                    <p className="text-xs font-medium mb-1">Parameters:</p>
                    <ul className="text-xs space-y-1">
                      {Object.entries(tool.parameters).map(([param, type]) => (
                        <li key={param} className="flex items-center gap-2">
                          <span className="font-mono text-primary">{param}</span>
                          <span className="text-muted-foreground">: {type as string}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
